import React, { ReactNode } from 'react';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ArrowForwardIosOutlined from '@mui/icons-material/ArrowForwardIosOutlined';

import { ExportStatus, ReportExportStatus } from '../../../../../../../../core/api/lab.types';

export interface ExportReportButtonProps {
  exportResponse: ExportStatus | null;
  exportStatus: ReportExportStatus | string;
  downloadExport: () => void;
  createExport: (type: ReportExportStatus) => void;
  exportLoading: boolean;
  buttonType: ReportExportStatus;
  buttonCreateLabel: string;
  buttonDownloadLabel: string;
  buttonTestId: string;
  tooltip: string;
  description?: string;
  icon?: ReactNode;
}

function ExportReportButton({
  exportResponse,
  exportStatus,
  downloadExport,
  createExport,
  exportLoading,
  buttonType,
  buttonCreateLabel,
  buttonDownloadLabel,
  buttonTestId,
  tooltip,
  description,
  icon,
}: ExportReportButtonProps) {
  const isCurrentExport = exportStatus === buttonType;
  const isLoading = exportLoading && isCurrentExport;
  const hasDownload = Boolean(exportResponse?.exportLink && isCurrentExport);

  const handleClick = () => {
    if (hasDownload) {
      downloadExport();
      return;
    }

    createExport(buttonType);
  };

  const label = hasDownload ? buttonDownloadLabel : buttonCreateLabel;
  const defaultIcon = hasDownload ? <DownloadOutlined color="primary" /> : <DescriptionOutlined color="primary" />;

  return (
    <ButtonBase
      title={tooltip}
      data-testid={buttonTestId}
      onClick={handleClick}
      disabled={isLoading}
      sx={{
        display: 'block',
        width: '100%',
        height: '100%',
        textAlign: 'left',
        borderRadius: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          height: '100%',
          border: 1,
          borderColor: hasDownload ? 'success.light' : 'divider',
          bgcolor: hasDownload ? 'success.lighter' : 'primary.main',
          overflow: 'hidden',
          color: 'white',
          transition: 'border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
          '&:hover': {
            borderColor: hasDownload ? 'success.main' : 'primary.dark',
            bgcolor: hasDownload ? 'success.light' : 'primary.dark',
            transform: 'translateY(-2px)',
            boxShadow: 6,
          },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minHeight: 120,
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
              color: hasDownload ? 'success.main' : 'primary.main',
              flexShrink: 0,
            }}
          >
            {isLoading ? (
              <CircularProgress color="success" data-testid="export-loader" size={24} />
            ) : (
              icon || defaultIcon
            )}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              component="p"
              color={hasDownload ? 'success.dark' : 'text.white'}
              sx={{ fontWeight: 700 }}
            >
              {label}
            </Typography>

            {description && (
              <Typography
                variant="body2"
                component="p"
                color={hasDownload ? 'text.secondary' : 'text.white'}
                sx={{ mt: 0.5 }}
              >
                {description}
              </Typography>
            )}

            <Typography
              variant="button"
              component="p"
              color={hasDownload ? 'success.dark' : 'text.white'}
              sx={{ mt: 1, fontWeight: 700 }}
            >
              {isLoading ? 'Creating report...' : hasDownload ? 'Download Report' : 'Create report'}
            </Typography>
          </Box>

          <ArrowForwardIosOutlined color={hasDownload ? 'success' : 'inherit'} sx={{ fontSize: 16, flexShrink: 0 }} />
        </Box>
      </Paper>
    </ButtonBase>
  );
}

export default ExportReportButton;
